import { SLOTS_APIS } from "@/api_framework/api_config";
import {
  IBookingCartPreviewResponseData,
  IBookingSummaryResponse,
  IBookingSummaryResponseData,
} from "@/api_framework/api_modals/Bookings";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import {
  setBookingCartPreviewData,
  setRescheduleBookingSummaryData,
  setSelectedPurchaseAddOnIds,
  setSelectedPurchaseMembershipId,
  setSelectedPurchaseMembershipPayAtClinic,
  setSelectedPurchaseServiceId,
} from "@/app_redux/reducers/slices/auth/slots_slice";
import axios, { AxiosError, AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback, useState } from "react";
import { getSummaryDiagnosticItems, toDiagnosticCartItem } from "@/app/utils/bookingServiceSelection";
import { useConfiguration } from "../../useConfiguration";
import { useCurlLogger } from "../../useCurlLogger";

AxiosCurlirize(axios, () => {});

export const useBookingSummaryAPI = () => {
  const dispatch = useAppDispatch();
  const configuration = useConfiguration();
  const { sendCurl } = useCurlLogger();
  const [isBookingSummaryInProgress, setIsBookingSummaryInProgress] = useState(false);

  const execute = useCallback(
    async (bookingId: string) => {
      if (!bookingId) {
        dispatch(setRescheduleBookingSummaryData({ data: null }));
        return null;
      }

      setIsBookingSummaryInProgress(true);
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const response = await axios
          .get(`${SLOTS_APIS?.BOOKING_SUMMARY?.baseURL ?? ""}${bookingId}/`, {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          })
          .then((res: AxiosResponse<IBookingSummaryResponse>) => {
            if (res.data.status && res.data.data) {
              const summaryData: IBookingSummaryResponseData = res.data.data;
              const membershipItems = summaryData?.addons?.memberships ?? [];
              const addOnItems = summaryData?.addons?.add_on_services ?? [];
              const diagnosticItems = getSummaryDiagnosticItems(summaryData);
              const serviceId = summaryData?.service?.sub_service_id || summaryData?.service?.service_id || null;

              const previewData: IBookingCartPreviewResponseData = {
                cart_items: [
                  ...(serviceId
                    ? [
                        {
                          id: serviceId,
                          type: "service",
                          qty: 1,
                          name: summaryData?.service?.sub_service_name || summaryData?.service?.service_name || "Service",
                          price: Number(summaryData?.service?.price ?? 0),
                          discount: 0,
                          final_price: Number(summaryData?.service?.price ?? 0),
                          description: summaryData?.service?.type_name || null,
                        },
                      ]
                    : []),
                  ...addOnItems.map((item) => ({
                    id: item.id,
                    type: "service_add_ons",
                    qty: 1,
                    name: item.name,
                    price: Number(item.price ?? 0),
                    discount: Number(item.discount ?? Math.max(0, Number(item.price ?? 0) - Number(item.final_price ?? 0))),
                    final_price: Number(item.final_price ?? item.price ?? 0),
                    discount_percent: Number(item.discount_percent ?? 0),
                    description: item.description || null,
                    duration: item.duration || null,
                    image_url: item.image_url || undefined,
                  })),
                  ...diagnosticItems.map(toDiagnosticCartItem),
                  ...membershipItems.map((item) => ({
                    id: item.id,
                    type: "membership",
                    qty: 1,
                    pay_at_clinic: false,
                    name: item.name,
                    price: Number(item.price ?? 0),
                    discount: Number(item.discount ?? Math.max(0, Number(item.price ?? 0) - Number(item.final_price ?? 0))),
                    final_price: Number(item.final_price ?? item.price ?? 0),
                    discount_percent: Number(item.discount_percent ?? 0),
                    duration: item.duration || null,
                    details: Array.isArray(item.details) ? item.details.join(", ") : item.details || null,
                    image_url: item.image_url || undefined,
                  })),
                ],
                billing_details: {
                  total_price: Number(summaryData?.billing_details?.total_price ?? 0),
                  total_discount: Number(summaryData?.billing_details?.total_discount ?? 0),
                  total_amount_to_pay: Number(summaryData?.billing_details?.total_amount_to_pay ?? 0),
                  amount_to_pay_at_clinic: Number(summaryData?.billing_details?.amount_to_pay_at_clinic ?? 0),
                  amount_to_pay_now: Number(summaryData?.billing_details?.amount_to_pay_now ?? 0),
                  booking_fee: Number(summaryData?.billing_details?.booking_fee ?? 0),
                  booking_fee_chip: summaryData?.billing_details?.booking_fee_chip ?? null,
                },
              };

              dispatch(setRescheduleBookingSummaryData({ data: summaryData }));
              dispatch(setSelectedPurchaseServiceId(serviceId));
              dispatch(setSelectedPurchaseAddOnIds({ data: addOnItems.map((item) => item.id) }));
              dispatch(setSelectedPurchaseMembershipId(membershipItems?.[0]?.id ?? null));
              dispatch(setSelectedPurchaseMembershipPayAtClinic(false));
              dispatch(setBookingCartPreviewData({ data: previewData }));

              return summaryData;
            }

            dispatch(setRescheduleBookingSummaryData({ data: null }));
            return null;
          })
          .catch((e: AxiosError) => {
            if (e.config?.["curlCommand"]) {
              sendCurl(e.config["curlCommand"]);
            }
            dispatch(setRescheduleBookingSummaryData({ data: null }));
            return null;
          });

        return response;
      } finally {
        setIsBookingSummaryInProgress(false);
      }
    },
    [configuration, dispatch, sendCurl],
  );

  return {
    execute,
    isBookingSummaryInProgress,
  };
};
